import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MeetingTypeAvailabilityApiService } from '../../../services/meeting-type-availability-api.service';
import {
  MeetingTypeAvailabilityRequest,
  MeetingTypeAvailabilityResponse,
} from '../../../models/meeting.model';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';

const DAYS = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
];

interface EditState {
  start: string;
  end: string;
}

@Component({
  selector: 'app-meeting-type-availability',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './meeting-type-availability.component.html',
})
export class MeetingTypeAvailabilityComponent implements OnChanges {
  @Input() typeId!: number;

  readonly days = DAYS;
  items: MeetingTypeAvailabilityResponse[] = [];
  loading = false;
  saving = false;

  // Alta de nueva franja
  newDay = 1;
  newStart = '09:00';
  newEnd = '17:00';

  // Edición inline por id
  editingId: number | null = null;
  edit: EditState = { start: '', end: '' };

  constructor(
    private api: MeetingTypeAvailabilityApiService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (c['typeId'] && this.typeId) this.load();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  dayLabel(day: number): string {
    return this.days.find((d) => d.value === day)?.label || '—';
  }

  timeString(v: string | null | undefined): string {
    return v ? v.slice(0, 5) : '';
  }

  load(): void {
    if (!this.typeId) return;
    this.loading = true;
    this.api.list(this.typeId, this.businessId).subscribe({
      next: (data) => {
        this.items = [...data].sort(
          (a, b) =>
            a.dayOfWeek - b.dayOfWeek ||
            this.timeString(a.startTime).localeCompare(this.timeString(b.startTime))
        );
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  add(): void {
    if (!this.newStart || !this.newEnd || !this.typeId) return;
    this.saving = true;
    const body: MeetingTypeAvailabilityRequest = {
      businessId: this.businessId,
      dayOfWeek: this.newDay,
      startTime: this.newStart,
      endTime: this.newEnd,
    };
    this.api.create(this.typeId, body).subscribe({
      next: () => {
        this.saving = false;
        this.load();
        this.toast.created('Franja horaria', `Se creó "${this.newStart} - ${this.newEnd}" correctamente.`);
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al crear la franja horaria',
        });
      },
    });
  }

  startEdit(item: MeetingTypeAvailabilityResponse): void {
    this.editingId = item.id;
    this.edit = {
      start: this.timeString(item.startTime),
      end: this.timeString(item.endTime),
    };
  }

  cancelEdit(): void {
    this.editingId = null;
  }

  saveEdit(item: MeetingTypeAvailabilityResponse): void {
    if (!this.edit.start || !this.edit.end) return;
    this.saving = true;
    const body: MeetingTypeAvailabilityRequest = {
      businessId: this.businessId,
      dayOfWeek: item.dayOfWeek,
      startTime: this.edit.start,
      endTime: this.edit.end,
    };
    this.api.update(this.typeId, item.id, body).subscribe({
      next: () => {
        this.saving = false;
        this.editingId = null;
        this.load();
        this.toast.updated('Franja horaria', `Se actualizó "${this.edit.start} - ${this.edit.end}" correctamente.`);
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al actualizar la franja horaria',
        });
      },
    });
  }

  async remove(item: MeetingTypeAvailabilityResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar franja horaria',
      message: '¿Eliminar esta franja horaria?',
    });
    if (!ok) return;
    this.api.delete(this.typeId, item.id, this.businessId).subscribe({
      next: () => {
        this.load();
        this.toast.deleted('Franja horaria', `Se eliminó "${this.timeString(item.startTime)} - ${this.timeString(item.endTime)}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar la franja horaria',
        });
      },
    });
  }
}
